import { useCallback, useState } from "react";
import cx from "classnames";
import { Button } from "@radix-ui/themes";
import { CircleBackslashIcon, CheckCircledIcon } from "@radix-ui/react-icons";
import styles from "./QaScore.module.scss";

export interface QaScoreButtonProps {
  value: number;
  active: boolean;
  updateScore?: (newScore: number) => Promise<void>;
  variant?: "number" | "binary";
  label?: string;
  hint?: string;
  hintAlign?: "left" | "center" | "right";
  viewOnly?: boolean;
}

export function QaScoreButton({
  value,
  active,
  updateScore,
  variant = "number",
  label,
  hint,
  hintAlign = "center",
  viewOnly = false,
}: QaScoreButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleOnClick = useCallback(async () => {
    if (active || updateScore == null) {
      return;
    }

    setIsLoading(true);
    await updateScore(value);
    setIsLoading(false);
  }, [active, value, updateScore]);

  return (
    <Button
      size={"1"}
      variant={"outline"}
      color={"gray"}
      className={cx(styles.QaScoreButton, styles[`value-${variant}-${value}`], {
        [styles.active]: active,
        [styles.hasHint]: hint != null,
        [styles.readonly]: updateScore == null || viewOnly,
      })}
      onClick={handleOnClick}
      loading={isLoading}
    >
      {variant === "binary" && (
        <>
          {value === 0 && <CircleBackslashIcon />}
          {value === 1 && <CheckCircledIcon />}
        </>
      )}

      {label ?? value}

      {hint != null && (
        <div className={cx(styles.QaScoreButtonHint, styles[hintAlign])}>
          {hint}
        </div>
      )}
    </Button>
  );
}
